// Brand preview component that shows a live preview of the brand as it's being created
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Database } from "@/database.types";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Globe, MapPin, Target, Sparkles } from "lucide-react";

type Brand = Database['public']['Tables']['brands']['Row']

export interface BrandPreviewProps {
  keyFields: [string, any][]
  formValues: Partial<Brand>
  currentStep: number
  getCurrentValue: (fieldName: keyof Brand) => string | number | boolean | null
  onApply?: (field: keyof Brand, content: string) => void
}

export function BrandPreview({ keyFields, getCurrentValue, formValues }: BrandPreviewProps) {
  const brandName = getCurrentValue('brand_name') || 'Your Brand Name';
  const [brandImages, setBrandImages] = useState<Array<{url: string, description?: string}>>([]);

  // Extract images from brand data
  useEffect(() => {
    const images: Array<{url: string, description?: string}> = [];

    // Check for images in metadata
    if (formValues.metadata && typeof formValues.metadata === 'object') {
      const metadata = formValues.metadata as any;
      if (metadata.images && Array.isArray(metadata.images)) {
        metadata.images.forEach((img: any) => {
          if (img.url) {
            images.push({ url: img.url, description: img.description });
          }
        });
      }
    }

    // Check for images in web_site_scraping_results
    if (formValues.web_site_scraping_results && typeof formValues.web_site_scraping_results === 'object') {
      const scrapingResults = formValues.web_site_scraping_results as any;
      if (scrapingResults.extracted_info?.images && Array.isArray(scrapingResults.extracted_info.images)) {
        scrapingResults.extracted_info.images.forEach((img: any) => {
          if (img.url && !images.some(i => i.url === img.url)) {
            images.push({ url: img.url, description: img.description });
          }
        });
      }
    }

    // Limit to 4 images for the gallery
    setBrandImages(images.slice(0, 4));
  }, [formValues]);

  // Group fields by their group name, ensuring each field appears only once
  const groupedFields = keyFields.reduce((acc, [fieldName, metadata]) => {
    if (fieldName === 'brand_name') return acc; // Skip brand name as it's shown above
    
    const value = getCurrentValue(fieldName as keyof Brand);
    if (!value) return acc;

    // Use group as key, create array if doesn't exist
    if (!acc[metadata.group]) {
      acc[metadata.group] = new Map(); // Use Map to ensure unique fields
    }
    // Only add if not already present
    if (!acc[metadata.group].has(fieldName)) {
      acc[metadata.group].set(fieldName, { value, metadata });
    }
    return acc;
  }, {} as Record<string, Map<string, { value: string | number | boolean, metadata: any }>>);

  // Helper to get key brand info
  const niche = getCurrentValue('niche');
  const targetGeography = getCurrentValue('target_geography');
  const elevatorPitch = getCurrentValue('elevator_pitch');
  const brandUrl = getCurrentValue('url');
  const brandUrlString = typeof brandUrl === 'string' ? brandUrl : null;

  return (
    <Card className="relative overflow-hidden bg-white shadow-xl">
      <ScrollArea className="h-[calc(100vh-8rem)]">
        {/* Header Section */}
        <div className="relative px-8 pt-8 pb-6">
          {/* Brand Preview Label */}
          <div className="text-xs text-zinc-500 font-semibold tracking-[0.2em] uppercase text-center mb-4">
            <Sparkles className="inline w-3 h-3 mr-1" />
            BRAND PREVIEW
          </div>

          {/* Brand Name */}
          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-zinc-900 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {brandName}
          </motion.h1>

          {/* Static Image Gallery with Reflections */}
          {brandImages.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <div className={`grid gap-4 ${
                brandImages.length === 1 ? 'grid-cols-1 max-w-md mx-auto' :
                brandImages.length === 2 ? 'grid-cols-2 max-w-2xl mx-auto' :
                brandImages.length === 3 ? 'grid-cols-3 max-w-4xl mx-auto' :
                'grid-cols-4 max-w-5xl mx-auto'
              }`}>
                {brandImages.map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                    className="relative group"
                  >
                    {/* Main Image */}
                    <div className="relative overflow-hidden rounded-lg shadow-lg">
                      <img
                        src={image.url}
                        alt={image.description || `${brandName} image ${index + 1}`}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>

                    {/* Reflection */}
                    <div className="relative h-24 mt-1 overflow-hidden opacity-30">
                      <img
                        src={image.url}
                        alt=""
                        className="w-full h-48 object-cover transform rotate-180 -translate-y-24"
                        style={{
                          maskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%)',
                          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%)'
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Quick Info Pills */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {niche && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
              >
                <Target className="inline w-3 h-3 mr-1" />
                {niche}
              </motion.div>
            )}
            {targetGeography && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7 }}
                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
              >
                <MapPin className="inline w-3 h-3 mr-1" />
                {targetGeography}
              </motion.div>
            )}
            {brandUrlString && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 }}
                className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium"
              >
                <Globe className="inline w-3 h-3 mr-1" />
                {brandUrlString.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </motion.div>
            )}
          </div>

          {/* Elevator Pitch if available */}
          {elevatorPitch && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="mt-6 max-w-3xl mx-auto"
            >
              <p className="text-zinc-600 text-base leading-relaxed text-center italic">
                "{elevatorPitch}"
              </p>
            </motion.div>
          )}
        </div>

        {/* Brand Fields Section */}
        <div className="px-8 pb-8">
          <AnimatePresence mode="wait">
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 1 }}
            >
              {Object.entries(groupedFields).map(([group, fieldsMap], index) => (
                <motion.div
                  key={group}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 1 + index * 0.1 }}
                >
                  <div className="bg-zinc-50 rounded-lg p-4">
                    {/* Group header */}
                    <h3 className="font-medium text-zinc-600 text-sm mb-3">
                      {group}
                    </h3>

                    {/* Fields content */}
                    <div className="space-y-3">
                      {Array.from(fieldsMap.entries()).map(([fieldName, { value, metadata }], fieldIndex) => (
                        <div key={fieldName}>
                          {fieldIndex > 0 && <div className="h-px bg-zinc-200 my-3" />}
                          <div>
                            <div className="text-xs text-zinc-500 mb-1">
                              {metadata.description.split('.')[0]}
                            </div>
                            <div className="text-zinc-900 text-sm">
                              {value}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </ScrollArea>
    </Card>
  );
} 